import { z } from "zod";

export const TEMPLATE_IDS = [
  "newsroom",
  "bento-console",
  "editorial-longform",
] as const;

export const TemplateIdSchema = z.enum(TEMPLATE_IDS);
export type TemplateId = z.infer<typeof TemplateIdSchema>;

export const DEFAULT_TEMPLATE_ID = "newsroom" as const satisfies TemplateId;

export type TemplateDensity = "comfortable" | "compact" | "spacious";

export type TemplateDescriptor = {
  id: TemplateId;
  label: string;
  description: string;
  layoutComponent: string;
  paletteCssFile: string;
  density: TemplateDensity;
  headlineFont: string;
  bodyFont: string;
  intent: string;
};

export const TEMPLATE_DESCRIPTORS = Object.freeze({
  newsroom: Object.freeze({
    id: "newsroom",
    label: "Newsroom",
    description: "Sequential breaking-news dashboard with comfortable scanning density.",
    layoutComponent: "TemplateNewsroom",
    paletteCssFile: "newsroom.css",
    density: "comfortable",
    headlineFont: "Inter",
    bodyFont: "Inter",
    intent: "Canonical live outbreak newsroom.",
  }),
  "bento-console": Object.freeze({
    id: "bento-console",
    label: "Bento Console",
    description: "Compact 12-column operational console for high-density surveillance.",
    layoutComponent: "TemplateBentoConsole",
    paletteCssFile: "bento-console.css",
    density: "compact",
    headlineFont: "IBM Plex Sans",
    bodyFont: "IBM Plex Sans",
    intent: "Dashboard-first outbreak command surface.",
  }),
  "editorial-longform": Object.freeze({
    id: "editorial-longform",
    label: "Editorial Longform",
    description: "Reading-first report layout with full-bleed spatial evidence.",
    layoutComponent: "TemplateEditorialLongform",
    paletteCssFile: "editorial-longform.css",
    density: "spacious",
    headlineFont: "Instrument Serif",
    bodyFont: "Inter",
    intent: "Narrative public-health briefing.",
  }),
} satisfies Record<TemplateId, TemplateDescriptor>);

export function isTemplateId(value: unknown): value is TemplateId {
  return TemplateIdSchema.safeParse(value).success;
}
